import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Quote, Link as LinkIcon, Save, Loader2,
} from "lucide-react";
import { logisticsApi } from "@/lib/api";
import { toast } from "sonner";

function ToolbarButton({ active, onClick, children, label, testid }) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? "default" : "ghost"}
      onClick={onClick}
      data-testid={testid}
      aria-label={label}
      title={label}
      className="h-8 px-2"
    >
      {children}
    </Button>
  );
}

/**
 * Rich Text Editor (TipTap) pour le descriptif de commande.
 */
export default function OrderDescriptionEditor({ orderId, initialHtml = "", onSaved }) {
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // StarterKit v3 already ships Underline + Link; configure standalone imports below
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: initialHtml || "",
    onUpdate: () => setDirty(true),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[180px] focus:outline-none p-3 bg-white",
      },
    },
  });

  useEffect(() => {
    if (editor && initialHtml !== undefined && !editor.isFocused) {
      const current = editor.getHTML();
      if ((initialHtml || "<p></p>") !== current) {
        editor.commands.setContent(initialHtml || "");
        setDirty(false);
      }
    }
  }, [initialHtml, editor]);

  if (!editor) return null;

  const handleSave = async () => {
    if (!orderId) return;
    try {
      setSaving(true);
      const html = editor.getHTML();
      await logisticsApi.updateOrderDescription(orderId, html);
      toast.success("Descriptif enregistré");
      setDirty(false);
      onSaved?.(html);
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const addLink = () => {
    const url = window.prompt("URL du lien", "https://");
    if (!url) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border rounded-md overflow-hidden" data-testid="order-description-editor">
      <div className="flex flex-wrap items-center gap-1 border-b bg-slate-50 px-2 py-1">
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Gras"
          testid="rt-bold"
        ><Bold className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="Italique"
          testid="rt-italic"
        ><Italic className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label="Souligné"
          testid="rt-underline"
        ><UnderlineIcon className="h-4 w-4" /></ToolbarButton>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          label="Titre"
          testid="rt-h2"
        ><Heading2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Liste à puces"
          testid="rt-ul"
        ><List className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Liste numérotée"
          testid="rt-ol"
        ><ListOrdered className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label="Citation"
          testid="rt-quote"
        ><Quote className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("link")}
          onClick={addLink}
          label="Lien"
          testid="rt-link"
        ><LinkIcon className="h-4 w-4" /></ToolbarButton>

        <div className="ml-auto flex items-center gap-2">
          {dirty && (
            <span className="text-xs text-amber-600" data-testid="rt-dirty-indicator">
              Modifications non sauvegardées
            </span>
          )}
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={saving || !dirty}
            data-testid="rt-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Enregistrer
          </Button>
        </div>
      </div>
      <EditorContent editor={editor} data-testid="rt-content" />
    </div>
  );
}
